import React, { lazy } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Location } from 'history';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { getLoginPath, renderRoutesArray } from '../../lib/helper/RouteHelper';
import { GOOGLE_RECAPTCHA_KEY } from '../../config/env';
import { QUERY_STRING } from '../../enums/QueryString';
import AppLoader from '../../components/AppLoader';
import PageKey from '../../pages/PageKey';
import useQuery from '../../lib/hook/router/useQuery';

const LazyAccessDenied = lazy(() => import('../../pages/AccessDeniedPage'));
const LazyChangePasswordDialog = lazy(
  () => import('../../components/ChangePasswordDialog')
);
const LazyForgotPassword = lazy(
  () => import('../../pages/Auth/ForgotPasswordPage')
);
const LazyLoginPage = lazy(() => import('../../pages/Auth/LoginPage'));
const LazyLogOut = lazy(() => import('../../pages/LogOutPage'));
const LazyNotFound = lazy(() => import('../../pages/NotFoundPage'));
const LazyOrganization = lazy(
  () => import('../../pages/Organization/OrganizationPage')
);
const LazyOrganizationsList = lazy(
  () => import('../../pages/Organization/OrganizationsPage')
);
const LazyPublicContent = lazy(
  () => import('../../pages/PublicContent/PublicContentPage')
);
const LazyReferenceRequest = lazy(
  () => import('../../pages/ReferenceRequest/ReferenceRequestPage')
);
const LazyResetPassword = lazy(
  () => import('../../pages/Auth/ResetPasswordPage')
);
const LazyRoomRedirection = lazy(
  () => import('../../pages/Room/RoomRedirectionPage')
);
const LazyRoomView = lazy(() => import('../../pages/Room/View/RoomViewPage'));
const LazyRootPage = lazy(() => import('../../pages/RootPage'));
const LazySignUp = lazy(() => import('../../pages/Auth/SignUpPage'));
const LazyZoomInstall = lazy(() => import('../../pages/Zoom/ZoomInstall'));

interface Props {
  hideLogin: boolean;
  isLoggedIn: boolean;
  isNewUser: boolean;
  location: Location;
}

const AppRoutes: React.FC<Props> = (props: Props): JSX.Element => {
  const { getQueryStringParam } = useQuery();

  const { hideLogin, isLoggedIn, isNewUser, location } = props;

  // ~~~~~~~~~~~~~~~ auth routes (log in, sign up, forgot & reset password) ~~~~~~~~~~~~~~~ //

  /**
   * There are 3 scenarios for the authentication routes:
   *  1. A user is already logged in: they're redirected to the respective
   *     next path (if any) or to their landing page (library or DSR)
   *  2. Not logged in: in this case we do want them to log in, so they have
   *     access to these authentication routes
   *  3. Hide login: special scenario which hides the log in UI, in particular
   *     for vendor mode (Mindtickle), to give a seamless experience when the
   *     log in happens automatically for the user
   */
  const renderAuthPage = (AuthComponent: React.ComponentType) => {
    if (isLoggedIn) {
      return (
        <Navigate replace to={getQueryStringParam(QUERY_STRING.NEXT) || '/'} />
      );
    }

    return hideLogin ? <AppLoader /> : <AuthComponent />;
  };

  const renderAuthRoutes = () =>
    renderRoutesArray([
      <Route
        element={renderAuthPage(LazyLoginPage)}
        key={PageKey.LogIn}
        path={PageKey.LogIn}
      />,
      <Route
        element={renderAuthPage(LazyResetPassword)}
        key={PageKey.ResetPassword}
        path={PageKey.ResetPassword}
      />,
      <Route
        element={renderAuthPage(LazySignUp)}
        key={PageKey.SignUp}
        path={PageKey.SignUp}
      />,
      <Route
        element={renderAuthPage(LazyForgotPassword)}
        key={PageKey.ForgotPassword}
        path={PageKey.ForgotPassword}
      />,
    ]);

  // ~~~~~~~~~~~~~~~ common routes (public & private) ~~~~~~~~~~~~~~~ //

  const renderCommonRoutes = () => [
    <Route
      element={<LazyRoomView />}
      key={PageKey.ContentLink}
      path={`${PageKey.ContentLink}/:roomUUID`}
    />,
    <Route
      element={<LazyRoomView />}
      key={PageKey.Room}
      path={`${PageKey.Room}/:roomUUID`}
    />,
    <Route
      element={<LazyReferenceRequest />}
      key={PageKey.ReferenceRequest}
      path={`${PageKey.ReferenceRequest}/:referenceRequestUUID/*`}
    />,
    // @todo deprecated
    <Route
      element={<LazyPublicContent />}
      key="legacy-content-link"
      path="content/:slug"
    />,
  ];

  // ~~~~~~~~~~~~~~~ private (require login) routes ~~~~~~~~~~~~~~~ //

  const renderOrganizationRoute = (
    Component: React.LazyExoticComponent<React.FC<unknown>>
  ) => {
    const next = getQueryStringParam(QUERY_STRING.NEXT);
    return next ? <Navigate replace to={next} /> : <Component />;
  };

  const renderPrivateRoutes = (overwrittenElement?: JSX.Element) =>
    renderRoutesArray([
      <Route
        element={overwrittenElement ?? <LazyLogOut />}
        key={PageKey.LogOut}
        path={PageKey.LogOut}
      />,
      <Route
        element={
          overwrittenElement ?? renderOrganizationRoute(LazyOrganization)
        }
        key={PageKey.Organization}
        path={`${PageKey.Organization}/:organizationId/*`}
      />,
      <Route
        element={<Outlet />}
        key={PageKey.Organizations}
        path={PageKey.Organizations}
      >
        <Route
          element={
            overwrittenElement ?? renderOrganizationRoute(LazyOrganizationsList)
          }
          path=""
        />
      </Route>,
      <Route
        element={overwrittenElement ?? <LazyZoomInstall />}
        key={PageKey.Zoom}
        path={PageKey.Zoom}
      />,
      <Route
        element={overwrittenElement ?? <LazyAccessDenied />}
        key={PageKey.Denied}
        path={PageKey.Denied}
      />,
    ]);

  // ~~~~~~~~~~~~~~~ public routes ~~~~~~~~~~~~~~~ //

  const renderRedirectFromPrivateToLogin = () => (
    <Navigate to={getLoginPath(location)} />
  );

  const renderPublicRoutes = () => [
    <Route
      key="sign-in"
      element={
        <GoogleReCaptchaProvider
          reCaptchaKey={GOOGLE_RECAPTCHA_KEY}
          scriptProps={{ async: true }}
        >
          <Outlet />
        </GoogleReCaptchaProvider>
      }
    >
      {renderAuthRoutes()}
    </Route>,
    <Route
      element={<LazyRoomRedirection />}
      key="edit-room-redirection"
      path={`${PageKey.Organization}/:organizationId/${PageKey.Room}/:action/:roomUUID`}
    />,
    /**
     * Overwritting the private routes render method with a redirect to the
     * login page. This is for scenarios where users try to access a private page
     * like rooms list or content details and they haven't logged in, so they're
     * redirected to the login page with the `next` query string to guide them
     * to the page they were expecting.
     *
     * @note the original idea was to wrap those routes but the <Routes>
     * component doesn't like anything that isn't a <Route> component as the
     * first level for its children
     */
    ...renderPrivateRoutes(renderRedirectFromPrivateToLogin()),
  ];

  const routes = isLoggedIn
    ? [...renderAuthRoutes(), ...renderPrivateRoutes()]
    : renderPublicRoutes();

  return (
    <>
      <Routes>
        <Route
          element={
            <LazyRootPage isLoggedIn={isLoggedIn} isNewUser={isNewUser} />
          }
          path="/"
        />
        {renderRoutesArray(renderCommonRoutes())}
        {renderRoutesArray(routes)}
        <Route element={<LazyNotFound basePath="./" />} path="*" />
      </Routes>
      <LazyChangePasswordDialog />
    </>
  );
};

export default AppRoutes;
